import { useState } from 'react';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import { PermissionButton } from 'src/routes/components/permission-button';
import { ProviderItem, incidentSweep } from '../../../services/xbitRelaySweepService';
import { XBIT_RELAY_SWEEP_PERMISSION_RESOURCE } from './constants';

type Props = {
  providers: ProviderItem[];
  onClose(): void;
};

export function IncidentSweepDialog({ providers, onClose }: Props) {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [provider, setProvider] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!provider) return;
    setLoading(true);
    try {
      const { data } = await incidentSweep({ provider });
      notify(data.message || t('module.xbitRelaySweepMonitor.incidentQueued'), {
        type: 'success',
      });
      onClose();
      refresh();
    } catch (err: any) {
      notify(err?.response?.data?.message || err?.message || t('common.error'), {
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('module.xbitRelaySweepMonitor.incidentTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">
            {t('module.xbitRelaySweepMonitor.incidentConfirm')}
          </Typography>
          <TextField
            select
            label={t('module.xbitRelaySweepMonitor.provider')}
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            size="small"
            fullWidth
          >
            {providers.map((p) => (
              <MenuItem key={p.provider} value={p.provider}>
                {p.provider}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={loading}>
          {t('actions.cancel')}
        </Button>
        <PermissionButton resource={XBIT_RELAY_SWEEP_PERMISSION_RESOURCE} action="incident">
          <Button
            variant="contained"
            color="error"
            onClick={handleSubmit}
            disabled={loading || !provider}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {t('module.xbitRelaySweepMonitor.incidentSweep')}
          </Button>
        </PermissionButton>
      </DialogActions>
    </>
  );
}
